import React, { useState, useEffect, useRef, useContext } from 'react';
import { FirebaseNewContext } from '../firebase/FirebaseNewContext';

const LocalMusicPlayer = ({ play = true }) => {
  const { firebase } = useContext(FirebaseNewContext) || {};
  const [remoteSongUrls, setRemoteSongUrls] = useState([]);
  const [loadingRemote, setLoadingRemote] = useState(true);

  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const audioRef = useRef(null);

  // Build playlist: prefer remote (Firebase) if available, otherwise use imported local songs
  const playlist = remoteSongUrls;

  // Initialize currentSongIndex to a random index once playlist is known
  useEffect(() => {
    if (playlist.length > 0) {
      setCurrentSongIndex(Math.floor(Math.random() * playlist.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist.length]);

  const playNextSong = () => {
    setCurrentSongIndex(prevIndex => {
      if (playlist.length === 0) return prevIndex;
      let nextIndex = Math.floor(Math.random() * playlist.length);
      if (playlist.length > 1 && nextIndex === prevIndex) {
        nextIndex = (nextIndex + 1) % playlist.length;
      }
      return nextIndex;
    });
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.addEventListener('ended', playNextSong);
    }
    return () => {
      if (audioEl) {
        audioEl.removeEventListener('ended', playNextSong);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist.length]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      if (play) {
        audioRef.current.play().catch(err => {
          console.error('Auto-play failed:', err);
        });
      }
    }
  }, [currentSongIndex, play, playlist]);

  // Fetch remote songs from Firebase Storage (global bucket path)
  useEffect(() => {
    let mounted = true;
    const loadRemote = async () => {
      if (!firebase || typeof firebase.getPresentationMusicList !== 'function') {
        setLoadingRemote(false);
        return;
      }
      try {
        const items = await firebase.getPresentationMusicList('global');
        if (!mounted) return;
        const urls = (items || []).map(i => i.url).filter(Boolean);
        setRemoteSongUrls(urls);
      } catch (e) {
        console.error('Error loading remote presentation music:', e);
      } finally {
        setLoadingRemote(false);
      }
    };
    loadRemote();
    return () => { mounted = false; };
  }, [firebase]);

  // Determine current source URL depending on playlist type
  const currentSongUrl = playlist.length > 0 ? playlist[currentSongIndex] : null;

  const mimeTypeFromUrl = (url) => {
    if (!url) return '';
    const lower = url.toLowerCase();
    if (lower.endsWith('.mp3')) return 'audio/mpeg';
    if (lower.endsWith('.m4a') || lower.endsWith('.mp4')) return 'audio/mp4';
    return '';
  };

  return (
    <audio
      ref={audioRef}
      // Hide the audio element while ensuring it's rendered.
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        opacity: 0,
        pointerEvents: 'none'
      }}
    >
      {currentSongUrl ? (
        <source src={currentSongUrl} type={mimeTypeFromUrl(typeof currentSongUrl === 'string' ? currentSongUrl : '')} />
      ) : null}
      Your browser does not support the audio element.
    </audio>
  );
};

export default LocalMusicPlayer;

